import { splitTimeItem } from 'helpers/bookingTemplates';
import { IScheduleItem } from 'models/shop/schedule';
import moment = require('moment');
import * as React from 'react';
import { momentWithTimezone } from 'redux/modules/shop/helpers';
import DraggableEvent from './DraggableEvent';
import { assignSubColumnsToItems } from 'containers/Shop/Helpers';
import renderIcon from 'helpers/renderIcon';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
const classNames = require('classnames');

const priorities: IScheduleItem['type'][] = ['event', 'available', 'empty', 'partial'];

interface IProps {
	items: IScheduleItem[];
	/**
	 * [Hours]
	 */
	step: number;
	start: number;
	/**
	 * [Hours] Tics may not show if no multiple of split is a full number
	 */
	split: number;
	topCoordinateRef?: React.MutableRefObject<any>;
	today: boolean;
	highlightedGroupId?: string;
	timezone?: string;
	columnWidth?: number;

	t?: any;
}

const getMaxProp = (items: IScheduleItem[], prop: 'column' | 'start' | 'end'): number =>
	items.reduce((acc, val) => (val[prop] > acc ? val[prop] : acc), 0);

// Realtime clock variables stored in an object
type Clock = {
	maxTimeSet: number;
	startTime: number;
	unit_heightSet: number;
	ref_currentTimeSet: React.MutableRefObject<any>;
	currentTimeContainer_HeightSet: number;
	topCoordinateHeightSet: number;
	timezone: string;
};

const clock: Clock = {
	maxTimeSet: undefined,
	startTime: undefined,
	unit_heightSet: undefined,
	ref_currentTimeSet: undefined,
	currentTimeContainer_HeightSet: undefined,
	topCoordinateHeightSet: undefined,
	timezone: undefined,
};

function normalizeItems<TItem extends IScheduleItem>(items: TItem[], step: number, timezone: string): TItem[] {
	if (step <= 0) {
		return items;
	}

	const maxColumn = getMaxProp(items || [], 'column');
	let ret: TItem[] = [];
	for (let i = 0; i <= maxColumn; i++) {
		const columnItems = items.filter((item) => item.column === i);
		const colResult: TItem[] = [];
		for (const priority of priorities) {
			const prioritized = columnItems.filter((item) => item.type === priority);
			if (priority == 'event') {
				colResult.push(...prioritized);
				continue;
			}

			for (const item of prioritized) {
				//let itemParts: TItem[] = [];
				// if (item.type === 'partial') {
				//   itemParts = [item];
				// } else {
				//   itemParts = splitTimeItem(item, colResult);
				// }
				colResult.push(...splitTimeItem(item, colResult, false, timezone));
			}
		}
		ret = [...ret, ...colResult];
	}
	const byStep: TItem[] = [];

	for (const item of ret) {
		if (item.atomic === true) {
			byStep.push(item);
			continue;
		}
		let itemStep = step;
		if (typeof item.atomic === 'number' && item.atomic) {
			itemStep = item.atomic;
		}
		const max = item.end + itemStep / 6; // add 1/6 of step to fix float comparison problems
		// const max = item.end - itemStep + itemStep/6; // add 1/6 of step to fix float comparison problems
		let iMoment = item.startTimeMoment;
		for (let i = item.start; i < max; ) {
			const start = i;
			const startTimeMoment = momentWithTimezone(timezone, iMoment);
			let endTimeMoment = momentWithTimezone(timezone, iMoment)
				.add(Math.floor(itemStep), 'hours')
				.add(Math.floor((itemStep - Math.floor(itemStep)) * 60), 'minutes');
			let end = i + itemStep;
			if (end > item.end) {
				end = item.end;
				endTimeMoment = item.endTimeMoment;
			}
			if (itemStep !== step) {
				const nextSplit = Math.floor(end / itemStep) * itemStep;
				if (nextSplit > start) {
					end = nextSplit;

					// convert time to unix, diff from start of day and round to itemStep
					const itemStepUnix = itemStep;
					const startOfDayUnix = momentWithTimezone(timezone, item.endTimeMoment).startOf('days').unix();
					// not sure if we need to use timezone here
					endTimeMoment = moment.unix(
						Math.floor((endTimeMoment.unix() - startOfDayUnix) / itemStepUnix / 3600) * 3600 * itemStepUnix +
							startOfDayUnix,
					);
				}
			}
			if (start === end) {
				break;
			}
			byStep.push({
				...item,
				start,
				startTimeMoment,
				end,
				endTimeMoment,
			});
			i = end;
			iMoment = endTimeMoment;
		}
	}

	if (items[0]?.type === 'holiday') {
		byStep.unshift({
			...byStep[0],
			...items[0],
		});
	}
	return byStep;
}

const renderCurrentTime = (
	maxTime?: number,
	len?: number,
	unit_height?: number,
	ref_currentTime?: React.MutableRefObject<any>,
	currentTimeContainer_Height?: number,
	topCoordinateHeight?: number,
	init?: boolean,
	timezone?: string,
) => {
	if (init) {
		// init the variables on first call, otherwise they will be undefined on interval update
		clock.maxTimeSet = maxTime;
		clock.startTime = len;
		clock.unit_heightSet = unit_height;
		clock.ref_currentTimeSet = ref_currentTime;
		clock.currentTimeContainer_HeightSet = currentTimeContainer_Height;
		clock.topCoordinateHeightSet = topCoordinateHeight;
		clock.timezone = timezone;
	}

	const currentTime = momentWithTimezone(clock.timezone).format('HH:mm');
	const hours = parseInt(currentTime.split(':')[0]);
	const minutes = parseInt(currentTime.split(':')[1]);
	const units_per_hour = 8;
	const time_unit = 60 / units_per_hour; // 1 unit = unit_height px, 1 hour = 8 units, 1 unit = 7.5 minutes

	const minTime = clock.startTime; // len - 1 because there is 1 * 0.125 hour added to maxTime on calendar

	let minMinutes = 0;
	let maxMinutes = 0;

	const minHours = Math.floor(minTime);
	if (minTime % 1 != 0) {
		minMinutes = (minTime % 1) * 60;
	}

	if (clock.maxTimeSet % 1 != 0) {
		maxMinutes = (clock.maxTimeSet % 1) * 60;
	}

	if (
		hours > clock.maxTimeSet ||
		(hours === clock.maxTimeSet && minutes > maxMinutes) ||
		hours < minHours ||
		(hours == minHours && minMinutes > minutes)
	) {
		// do not show time outside working hours
		return undefined;
	}

	let hoursFromStart = hours - minHours; // get the number of hours from start of working hours
	let minutesLeft = minutes - minMinutes; // get the remaining minutes from start of working hours

	if (minutesLeft < 0) {
		// if minMinutes > minutes we have to subtract 1hour and recalculate minutes
		hoursFromStart -= 1;
		minutesLeft = 60 + minutesLeft;
	}

	// this is the distance that the live clock is pushed from the top
	const top =
		(hoursFromStart * units_per_hour + Math.floor(minutesLeft / time_unit)) * clock?.unit_heightSet +
		((minutesLeft % time_unit) / time_unit) * clock?.unit_heightSet -
		(clock.currentTimeContainer_HeightSet
			? clock.currentTimeContainer_HeightSet
			: clock.ref_currentTimeSet?.current?.clientHeight) /
			2 +
		clock?.topCoordinateHeightSet; // 1 unit = unit_height px, 1 hour = 8 units, 1 unit = 7.5 minutes, currentTimeContainer_Height / 2 is the half the height of the timeContainer

	return (
		<div className="currentTime" key={1} style={{ top: `${top}px` }}>
			<div className="timeContainer" ref={clock.ref_currentTimeSet}>
				<div className="time">{currentTime}</div>
				<div className="line"></div>
			</div>
		</div>
	);
};

const renderHour = (h: number, ref: React.MutableRefObject<any>): JSX.Element => {
	let className: '' | 'full' | 'half' | 'quarter' = '';
	let isFull = false;
	if (!(h % 1)) {
		isFull = true;
	}

	if (!((h * 2) % 1)) {
		className = 'half';
	} else if (!((h * 4) % 1)) {
		className = 'quarter';
	}
	let hours;
	if (className === 'half' || isFull) {
		// the stupid, but effective hour format
		hours = Math.floor(h).toString().padStart(2, '0');
	}
	return (
		<span className={className} key={h} ref={ref}>
			<span>{hours && hours + (isFull ? ':00' : ':30')}</span>
		</span>
	);
};
const timeFromEvent = (
	event: IScheduleItem,
	type: IScheduleItem['type'] = 'empty',
	onClick: IScheduleItem['onClick'] = null,
	atomic: IScheduleItem['atomic'] = true,
): IScheduleItem => ({
	type,
	column: event?.column,
	start: event?.start,
	end: event?.end,
	startTimeMoment: event?.startTimeMoment,
	endTimeMoment: event?.endTimeMoment,
	atomic,
	onClick,
	resourceId: event.resourceId,
});
let Schedule: React.FC<IProps> = ({
	items,
	step,
	start,
	split,
	topCoordinateRef,
	today,
	highlightedGroupId,
	timezone,
	columnWidth,
	t,
}) => {
	const [height, setHeight] = React.useState(0);
	const [currentTimeContainer_Height, setCurrentTimeContainerHeight] = React.useState(0);
	const [currentTime, setCurrentTime] = React.useState<JSX.Element>(<></>);
	const [topCoordinateHeight, setTopCoordinateHeight] = React.useState(0);
	const [indexIsBeingDragged, setIndexIsBeingDragged] = React.useState(null);
	const ref = React.useRef(null);
	const ref_currentTime = React.useRef(null);
	const draggableAreaRef = React.useRef<HTMLDivElement>();
	// reference to all items that are not events (is reference to be acessible in callback functions)
	const nonEventsRef = React.useRef<IScheduleItem[]>(null);

	const [dragConstraintViolated, setDragConstraintViolated] = React.useState(false);
	const [normalizedItems, setNormalizedItems] = React.useState<IScheduleItem[]>(null);
	/**
	 * Add new appointment tooltip will be shown next to the item stored in {calendarSelectMenuItem}
	 */
	const [calendarSelectMenuItem, setCalendarSelectMenuItem] = React.useState<{
		item: IScheduleItem;
		style: React.CSSProperties;
	}>(undefined);
	const calendarSelectMenuItemRef = React.useRef<HTMLDivElement>();

	const refBoundingBox = draggableAreaRef?.current?.getBoundingClientRect();

	const rescheduleItem = (sourceItem: IScheduleItem, targetItem: IScheduleItem) => {
		sourceItem?.onClick(true, {
			startTimeMoment: momentWithTimezone(timezone, targetItem.startTimeMoment),
			endTimeMoment: momentWithTimezone(timezone, targetItem.startTimeMoment).add(
				moment.duration(moment(sourceItem.endTimeMoment).diff(moment(sourceItem.startTimeMoment))),
			),
			resourceId: targetItem?.resourceId,
		});
	};

	if (step <= 0) {
		return null;
	}

	const nitems = normalizedItems || [];
	const item2style = (item: IScheduleItem): React.CSSProperties => {
		const rowHeight = Math.round((step * 60) / (split || 1));
		const colWidth = columnWidth;

		let left = item.column * colWidth;
		if (item.subColumn && item.totalSubColumns) {
			left += (colWidth * item.subColumn) / item.totalSubColumns;
		}

		return {
			left,
			width: (colWidth * (item.subColSpan || 1)) / (item?.totalSubColumns || 1),
			top: Math.round((item.start - start) / step) * rowHeight,
			height: (Math.round((item.end - start) / step) - Math.round((item.start - start) / step)) * rowHeight,
		};
	};
	const calendarMenuItem2Props = (menuItem, className) => {
		if (!menuItem) {
			return undefined;
		}
		const width = 260;

		// Offset of the container relative to the screen 0
		const leftOffset = refBoundingBox ? -refBoundingBox.x : 0;

		let left = menuItem.style.left + menuItem.style.width;
		if (leftOffset + window.innerWidth - left - width < 0) {
			left = leftOffset + window.innerWidth - width;
		}

		const onLeftSide = menuItem.style.left + menuItem.style.width / 2 - leftOffset > window.innerWidth / 2;

		// Show on left side if element is on the other half
		if (onLeftSide) {
			left = menuItem.style.left - width;
			if (left < leftOffset) {
				left = leftOffset;
			}
		}

		return {
			className: className + (onLeftSide ? ' rightSideArrow' : ''),
			style: {
				left,
				top: menuItem.style.top,
				height: menuItem.style.height,
				width,
			},
		};
	};
	const maxTime = getMaxProp(items, 'end');
	const hours: JSX.Element[] = [];
	// 4 cells = 1 split

	if (start % 0.125 != 0) {
		// round the start time to the nearest lower 15 minutes
		start = Math.floor(start / 0.125) * 0.125;
	}

	for (let i = start; i <= maxTime; i += (split || 1) / 4) {
		hours.push(renderHour(i, ref));
	}
	// WE NEED TO GET THIS HEIGHTS TO CORRECTLY DISPLAY CURRENT TIME ON THE CALENDAR
	// get height of the container of 1 time unit
	React.useLayoutEffect(() => {
		if (ref.current) {
			setHeight(ref.current.clientHeight);
		}
	}, [ref.current]);

	// get height of the container of the current time
	React.useLayoutEffect(() => {
		if (ref_currentTime.current && ref && ref.current) {
			setCurrentTimeContainerHeight(ref?.current?.clientHeight);
		}

		clock.currentTimeContainer_HeightSet = currentTimeContainer_Height || ref_currentTime?.current?.clientHeight;
	}, [ref_currentTime.current]);

	// get height of the top coordinate
	React.useLayoutEffect(() => {
		// TASK 8015: Added ".clientHeight" to if statement otherwise the state would not be set correctly when switching from Appointments > Work Time > Appointments
		if (topCoordinateRef.current.clientHeight) {
			setTopCoordinateHeight(topCoordinateRef.current.clientHeight);
		}
	}, [topCoordinateRef.current]);

	// update the clock
	React.useEffect(() => {
		const interval = setInterval(() => {
			today && setCurrentTime(renderCurrentTime());
		}, 30000);

		return () => clearInterval(interval);
	}, []);

	// update the clock when changing the date / start/end time of the schedule
	React.useEffect(() => {
		if (currentTime && currentTime.key !== null) {
			clock.maxTimeSet = maxTime;
			clock.startTime = start;
			today && setCurrentTime(renderCurrentTime());
		}
	}, [start, maxTime]);

	// Normalize the items only when they were changed
	React.useEffect(() => {
		if (items) {
			const itemsWithSubColumns = assignSubColumnsToItems(items || []);
			const nItems = normalizeItems(itemsWithSubColumns, step, timezone);
			setNormalizedItems(nItems);
		}
	}, [items]);

	const availableEventTemplate = items.find((i) => i.type == 'available');

	let aditionalItems = [];
	nitems.forEach((item, i) => {
		if (item.type == 'event' || item.type == 'partial') {
			aditionalItems.push(timeFromEvent(item));
			if (indexIsBeingDragged == i && availableEventTemplate) {
				// Add available behind event if it is being dragged
				const availableBehindEvent = normalizeItems(
					[timeFromEvent(item, 'available', availableEventTemplate.onClick, split)],
					step,
					timezone,
				);
				availableBehindEvent?.shift();
				if (availableBehindEvent?.length) {
					aditionalItems = aditionalItems.concat(availableBehindEvent);
				}
			}
		}
	});

	const nonEvents = nitems.concat(aditionalItems).filter((i) => i.type != 'event');
	nonEventsRef.current = nonEvents;

	/**
	 * returns a function that checks if drop target is valid based on item drag constraints
	 * @param item the item that is being rescheduled
	 * @returns validator function that accepts target and returns true if target is valid
	 */
	const getItemValidityChecker = (item: IScheduleItem) => (target) => {
		const isViolatingConstraints = (target) => {
			if (target?.classList?.contains('scheduleTableItem')) {
				const targetI = target.getAttribute('data-index');
				const targetItem: IScheduleItem = nonEventsRef.current[targetI];

				const dayIsSameAsTarget = (e) =>
					momentWithTimezone(timezone, targetItem.startTimeMoment)
						.startOf('day')
						.isSame(momentWithTimezone(timezone, e.startTimeMoment).startOf('day'));

				// if item is in the past
				if (momentWithTimezone(timezone, targetItem?.startTimeMoment).diff(momentWithTimezone(timezone)) < 0) {
					return true;
				}

				if (!item?.dragConstraints) return false;

				// if day is in drag constraints
				if (Object.prototype.hasOwnProperty.call(item.dragConstraints, 'day')) {
					if (item.dragConstraints['day'] == 'same' && !dayIsSameAsTarget(item)) {
						// if day has changed
						return true;
					} else if (
						item.dragConstraints['day'] instanceof Array &&
						!item?.dragConstraints['day'].find((e) => dayIsSameAsTarget(e))
					) {
						// id day is not in allowed dates
						return true;
					}
				}

				// if resource is in drag constraints
				if (Object.prototype.hasOwnProperty.call(item.dragConstraints, 'resource')) {
					if (item.dragConstraints['resource'] == 'same' && targetItem.resourceId != item.resourceId) {
						// if resource has changed
						return true;
					} else if (
						item.dragConstraints['resource'] instanceof Array &&
						item.dragConstraints['resource'].indexOf(targetItem.resourceId) == -1
					) {
						// if resource is not in allowed resources
						return true;
					}
				}
				return false;
			}
			return true;
		};
		const violated = isViolatingConstraints(target);
		setDragConstraintViolated(violated);
		return !violated;
	};

	// scheduleTableHolderWrapper added for the gray area
	return (
		<div
			className={
				'scheduleTableHolder' +
				(indexIsBeingDragged != null ? ' dragInProgress' : '') +
				(dragConstraintViolated && indexIsBeingDragged != null ? ' constraintViolated' : '')
			}
		>
			<div className="hoursCoordinate">
				{hours}
				{today
					? currentTime &&
					  currentTime.key != null &&
					  currentTimeContainer_Height === clock.currentTimeContainer_HeightSet
						? currentTime
						: renderCurrentTime(
								maxTime,
								start,
								height,
								ref_currentTime,
								currentTimeContainer_Height,
								topCoordinateHeight,
								true,
								timezone,
						  )
					: null}
			</div>
			<div className="tableLayersWrapper" ref={draggableAreaRef}>
				{calendarSelectMenuItem && (
					<div {...calendarMenuItem2Props(calendarSelectMenuItem, 'calendarSelectMenuWrapperWrapper')}>
						<div className="calendarSelectMenuWrapper">
							<div className="calendarSelectMenuTriangle"></div>
							<div className="calendarSelectMenu">
								<div
									className="calendarSelectMenuItem"
									onClick={() =>
										calendarSelectMenuItem.item.onClick(calendarSelectMenuItem.item.startTimeMoment, 'main')
									}
								>
									{renderIcon('newAppointment', undefined, undefined, 'calendarSelectMenuItemIcon')}
									<p className="calendarSelectMenuItemText">{t('Calendar_SelectMenu_NewAppointment')}</p>
								</div>
								<div
									className="calendarSelectMenuItem"
									onClick={() =>
										calendarSelectMenuItem.item.onClick(
											calendarSelectMenuItem.item.startTimeMoment,
											'add-edit-blocked-time',
										)
									}
								>
									{renderIcon('blockedTime', undefined, undefined, 'calendarSelectMenuItemIcon')}
									<p className="calendarSelectMenuItemText">{t('Calendar_SelectMenu_NewBlockedTime')}</p>
								</div>
							</div>
						</div>
					</div>
				)}
				<div
					className="scheduleTable scheduleTableDrag"
					style={{ gridAutoRows: Math.round((step * 60) / (split || 1)) + 'px' }}
				>
					{nitems.map((item, i) => {
						const notInteractive =
							momentWithTimezone(timezone, item.endTimeMoment).diff(momentWithTimezone(timezone)) &&
							item.type == 'available';
						//events
						if (item.type == 'event') {
							return (
								<DraggableEvent
									bottomCoordLimit={{ x: refBoundingBox?.x, y: refBoundingBox?.y }}
									key={i}
									title={item.hint ? item.hint(item.start) : undefined}
									className={
										'scheduleTableItem ' +
										item.type +
										((item.start / (split || 1)) % 1 ? '' : ' fullHour') +
										(item.onClick && !notInteractive ? ' interactive' : '') +
										(highlightedGroupId && highlightedGroupId == item.highlightGroupId ? ' highlighted' : '')
									}
									style={item2style(item)}
									disabled={momentWithTimezone(timezone, item.endTimeMoment).diff(momentWithTimezone(timezone)) < 0}
									onClick={item.onClick && !notInteractive ? () => item.onClick(item.startTimeMoment) : undefined}
									onDragStart={() => {
										setIndexIsBeingDragged(i);
										setDragConstraintViolated(false);
									}}
									onDragEnd={(target) => {
										const targetValid = getItemValidityChecker(item)(target);
										if (
											targetValid &&
											target?.classList?.contains('scheduleTableItem') &&
											!target?.classList?.contains('empty')
										) {
											const targetI = target.getAttribute('data-index');
											rescheduleItem(item, nonEventsRef.current[targetI]);
										}
										setIndexIsBeingDragged(null);
									}}
									onTargetChanged={getItemValidityChecker(item)}
									onMouseEnter={item?.onMouseEnter}
									onMouseLeave={item?.onMouseLeave}
								>
									{item.title}
								</DraggableEvent>
							);
						}
					})}
				</div>
				<div className="scheduleTable" /*style={{ gridAutoRows: Math.round((step * 60) / (split || 1)) + 'px' }}*/>
					{nonEvents.map((item, i) => {
						// non events (not draggable)
						if (item?.type == 'holiday') {
							return null;
						}

						const notInteractive =
							momentWithTimezone(timezone, item.endTimeMoment).diff(momentWithTimezone(timezone)) < 0 &&
							item.type == 'available';
						return (
							<div
								ref={
									calendarSelectMenuItem && calendarSelectMenuItem.item == item ? calendarSelectMenuItemRef : undefined
								}
								data-index={i}
								key={i}
								title={item.hint ? item.hint(item.start) : undefined}
								className={classNames('scheduleTableItem', item.type, {
									fullHour: !((item.start / (split || 1)) % 1),
									interactive: item.onClick && !notInteractive,
									'partially-unavailable': item.containsPartiallyUnavailable,
									hover: calendarSelectMenuItem && calendarSelectMenuItem.item == item,
								})}
								style={item2style(item)}
								onClick={
									item.onClick && !notInteractive
										? () => {
												if (calendarSelectMenuItem?.item != item) {
													const handler = (e: Event) => {
														if (
															!(e?.target as any)?.classList?.contains('scheduleTableItem') ||
															!(e?.target as any)?.classList?.contains('available')
														) {
															setCalendarSelectMenuItem(undefined);
														}
														removeEventListener('scroll', handler);
														removeEventListener('click', handler);
													};

													addEventListener('scroll', handler);
													setTimeout(() => {
														addEventListener('click', handler);
													}, 100);
													setCalendarSelectMenuItem({ item, style: item2style(item) });
												}
										  }
										: undefined
								}
							>
								{item.title}
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
};

Schedule = translate(c.DEFAULT_TRANSLATION_NAMESPACE)(Schedule);
export { Schedule };
